import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch, apiBaseUrl } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface HashavshevetExportResponse {
  export_id: string;
  period: string;
  generated_at: string;
  download_url: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function buildHashavshevetUrl(baseUrl: string, period: string): string {
  return `${baseUrl}/finance/hashavshevet/${period}/export`;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceHashavshevetPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['finance', 'hashavshevet', period],
    queryFn: () => apiFetch<HashavshevetExportResponse>(`/finance/hashavshevet/${period}/export`),
    enabled: false,
  });

  const exportUrl = buildHashavshevetUrl(apiBaseUrl(), period);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Hashavshevet' },
      ]} />

      <div style={{ marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Hashavshevet — export
        </h1>
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)', marginTop: 4, fontFamily: 'var(--omega-mono)' }}>
          Export transactions file to Hashavshevet
        </div>
      </div>

      <div style={{
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 6, padding: '16px 20px', marginBottom: 24, maxWidth: 480,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Period:</label>
          <input
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="YYYY-MM"
            style={{ ...inputStyle, width: 100 }}
          />
          <button
            onClick={() => refetch()}
            disabled={isPending}
            style={{
              background: 'rgba(125,211,252,0.10)', border: '1px solid rgba(125,211,252,0.35)',
              borderRadius: 4, color: '#7dd3fc', fontFamily: 'var(--omega-mono)',
              fontSize: '8.5pt', fontWeight: 700, padding: '5px 14px', cursor: 'pointer',
            }}
          >
            {isPending ? 'Preparing…' : 'Export'}
          </button>
        </div>
        <div style={{ fontSize: '7.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
          {exportUrl}
        </div>
      </div>

      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Export error.</div>}

      {data && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <a
            href={data.download_url}
            download
            style={{
              display: 'inline-block',
              background: 'rgba(110,231,183,0.12)', border: '1px solid rgba(110,231,183,0.35)',
              borderRadius: 4, color: '#6ee7b7', fontFamily: 'var(--omega-mono)',
              fontSize: '8.5pt', fontWeight: 700, padding: '6px 16px', textDecoration: 'none',
            }}
          >
            Download file ↓
          </a>
          <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {data.export_id}
          </span>
        </div>
      )}
    </div>
  );
}
